import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";

import { checkSession, login, logout, confirmMfa } from "../api/User";
import { getPermissions } from "../api/Permissions";

import userState from "../state/userState";
import permissionsState from "../state/permissionsState";

import LoadingSpinner from "../components/shared/LoadingSpinner";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useRecoilState(userState.value);
  const setPermissions = useSetRecoilState(permissionsState);
  const hasFetchedSession = useRef(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasFetchedSession.current) return;
    hasFetchedSession.current = true;

    const fetchSessionAndPermissions = async () => {
      try {
        const session = await checkSession();

        if (session?.loggedIn) {
          setUser(session.user);

          try {
            const resp = await getPermissions(navigate, {});
            const perms = resp?.permissions ?? {};
            setPermissions({ ...perms, loaded: true });
          } catch (e) {
            setPermissions({ loaded: true });
          }
        } else {
          setPermissions(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSessionAndPermissions();
  }, [navigate, setPermissions, setUser]);

  const applyLoginData = async (userData) => {
    setUser({
      id: userData.user_id,
      first_name: userData.f_name,
      last_name: userData.l_name,
      email: userData.email,
    });

    try {
      const resp = await getPermissions(navigate, {});
      const perms = resp?.permissions ?? {};
      setPermissions({ ...perms, loaded: true });
    } catch {
      setPermissions({ loaded: true });
    }
  };

  const handleLogin = async (email, password) => {
    const data = await login({ email, password });

    if (data?.mfa?.required || data?.mfaSetupRequired) {
      return data;
    }

    await applyLoginData(data.user);
    return data;
  };

  const handleMfaConfirm = async (code) => {
    const data = await confirmMfa({ code });
    await applyLoginData(data.user);
    return data;
  };

  const handleLogout = async () => {
    await logout();
    setUser({ id: null, first_name: null, last_name: null, email: null });
    setPermissions(null);
  };

  return (
    <AuthContext.Provider value={{ user, handleLogin, handleMfaConfirm, handleLogout }}>
      {loading ? (
        <div className="flex min-h-screen flex-col items-center justify-center bg-brand-primary px-4 text-center font-poppins text-[#b6bfcc]">
          <img src="/myriad-logo.svg" alt="Logo" className="mb-4 h-[120px] max-w-full" />
          <h1 className="mt-0 mb-20 font-inter text-4xl font-semibold text-text-subtle">Myriad Lab</h1>
          <LoadingSpinner message="If this screen persists, please contact us!" />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
